import { forwardRef, useEffect, useRef } from "react";

import { faWarning } from "@fortawesome/pro-solid-svg-icons";
import mergeRefs from "merge-refs";
import { twMerge } from "tailwind-merge";

import { ErrorDetails } from "src/datasets/components/errorUtils";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

const STICKY_HEADER_OFFSET = 65;

type CellWrapperProps = {
  children: React.ReactNode;
  invalid?: boolean;
  disabled?: boolean;
  warning?: boolean;
  error?: ErrorDetails;
  postfix?: React.ReactNode;
  editing: boolean;
  selected: boolean;
};

export const CellWrapper = forwardRef<HTMLDivElement, CellWrapperProps>(
  (
    { children, invalid, disabled, warning, error, postfix, editing, selected },
    ref,
  ) => {
    const cellRef = useRef<HTMLDivElement>(null);

    useScrollIntoViewIf({
      condition: selected && !editing,
      ref: cellRef,
    });

    return (
      <div
        ref={mergeRefs(cellRef, ref)}
        className={twMerge(
          "h-full w-full max-w-full rounded-none font-inter-normal-12px text-gray-800",
          "flex items-center justify-between gap-x-1.5 px-1.5",
          "group",
          warning &&
            "outline outline-1 outline-yellow-100 group-[:not(.is-empty)]:bg-orange-50",
          disabled &&
            "text-gray-500 outline outline-1 outline-gray-100 group-[:not(.is-empty)]:bg-gray-50",
          invalid && "bg-red-50",
          "outline-0",
          selected && "bg-indigo-50 outline outline-1 outline-indigo-500",
          !editing && "select-none",
        )}
        data-loc="dataset-table-cell"
        style={{
          scrollMarginTop: `${STICKY_HEADER_OFFSET}px`,
          scrollMarginBottom: `1px`,
        }}
        tabIndex={-1}
      >
        {children}

        <div className="flex items-center gap-x-1.5">
          {error && (
            <Tooltip
              body={error.description}
              footerAction={
                error.applyFix && {
                  text: "Apply fix",
                  onClick: () => error.applyFix?.(),
                }
              }
              placement="right"
              title={error.message}
            >
              <Icon
                variant="error"
                dataLoc="dataset-cell-error-icon"
                icon={faWarning}
                size="2xs"
              />
            </Tooltip>
          )}

          {postfix}
        </div>
      </div>
    );
  },
);

const useScrollIntoViewIf = ({
  condition,
  ref,
}: {
  condition: boolean;
  ref: React.RefObject<HTMLDivElement>;
}) => {
  useEffect(() => {
    if (condition && ref.current) {
      ref.current.focus();
      ref.current.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [condition, ref]);
};
